import { getId } from '@artup/utilities'
import { Viewport } from '../../services'

export default {
  /**
   * Unique runtime ID
   * @type {String}
   */
  id: `${getId('application-')}-${(new Date()).getTime()}`,

  /**
   * Application initialization options
   * @type {ApplicationOptions}
   */
  options: null,

  /**
   * Indicates that the client has been initialized
   * @type {Boolean}
   */
  isInitialized: false,

  /**
   * Indicates that client initialization has failed
   * @type {Boolean}
   */
  initializationFailed: false,

  /**
   * Application details - client name, version and description from `package.json`
   * @type {Object}
   */
  application: {
    name: '',
    version: '',
    description: '',
    url: ''
  },

  /**
   * Applets installed into the application
   * @type {Dictionary<String, AppletOptions>}
   */
  applets: {},

  /**
   * Global configuration
   * @type {Object}
   */
  configuration: null,

  /**
   * Environment: `development`, `production`, `beta` etc.
   * @type {String}
   */
  environment: null,

  /**
   * Indicates that we're now redirecting to another URL
   * @type {Object}
   */
  redirecting: {
    /**
     * Message to show while redirecting
     * @type {String}
     */
    message: '',
    /**
     * URL to redirect to
     * @type {String}
     */
    url: ''
  },

  /**
   * Indicates that we're now performing a full reload
   * @type {Object}
   */
  reloading: {
    /**
     * Message to show while reloading
     * @type {String}
     */
    message: ''
  },

  /**
   * Indicates a running long-lasting activity in the client,
   * such as saving a record, retrieving data etc.
   * @type {Object}
   */
  activity: {
    /**
     * Indicates that the client is busy now
     * @type {Boolean}
     */
    now: false,
    /**
     * Start time of the activity
     * @type {Date}
     */
    startedAt: null,
    /**
     * End time of the activity
     * @type {Date}
     */
    finishedAt: null,
    /**
     * Activity duration in milliseconds
     * @type {Number}
     */
    duration: null,
    /**
     * Action with which we're busy
     * @type {String}
     */
    action: null,
    /**
     * Custom message describing the activity
     * @type {String}
     */
    message: null,
    /**
     * More detailed description of the activity
     * @type {String}
     */
    details: null,
    /**
     * Warning message registered at the end of the activity
     * @type {String}
     */
    warning: null,
    /**
     * Error message registered at the end of the activity
     * @type {String}
     */
    error: null,
    /**
     * Data associated with the activity
     * @type {any}
     */
    data: null,
    /**
     * Callback allowing to hide the notification about the activity.
     * This happens automatically with a call to `done()` action
     * but sometimes needs to be called before, i.e. in case of failure.
     * @type {Function}
     */
    hideNotification: null
  },

  /**
   * Last unrecoverable error
   * @type {Object}
   */
  error: {
    /**
     * User-friendly message
     * @type {String}
     */
    message: null,
    /**
     * Intercepted exception
     * @type {Error}
     */
    exception: null
  },

  /**
   * One-second ticker, useful for many things
   * @type {Number}
   */
  ticker: 0,

  /**
   * Click event, useful for many things
   * @type {Number}
   */
  click: {
    /**
     * Click counter
     * @type {Number}
     */
    count: 0,
    /**
     * Click event details
     * @type {MouseEvent}
     */
    event: null
  },

  /**
   * Indicates that client is now redirecting to another URL
   * @type {Boolean}
   */
  get isRedirecting () {
    return Boolean(this.redirecting.url)
  },

  /**
   * Indicates that client is now performing a full reload
   * @type {Boolean}
   */
  get isReloading () {
    return Boolean(this.reloading.url)
  },

  /**
   * Indicates that currently working in development environment
   * @type {Boolean}
   */
  get isDevelopmentEnvironment () {
    return this.environment === 'development'
  },

  /**
   * Indicates that currently working in beta environment
   * @type {Boolean}
   */
  get isBetaEnvironment () {
    return this.environment === 'beta'
  },

  /**
   * Indicates that currently working in production environment
   * @type {Boolean}
   */
  get isProductionEnvironment () {
    return this.environment === 'production'
  },

  /**
   * Returns company details
   * @type {Object}
   */
  get company () {
    return this.configuration ? this.configuration.company : {}
  },

  /**
   * Indicates an unrecoverable error
   * @type {Boolean}
   */
  get isError () {
    return Boolean(this.error.exception || this.error.message)
  },

  /**
   * Indicates that client is currently busy with a long-lasting activity
   * @type {Boolean}
   */
  get isBusy () {
    return this.activity.now
  },

  /**
   * Checks whether client is currently busy with the specified long-lasting activity
   * @type {Boolean}
   */
  isBusyWith (action) {
    return this.activity.now && this.activity.action === action
  },

  /**
   * Returns true if UI is running on mobile phone
   * which limits the available functionality and data
   * @type {Boolean}
   */
  get isMobilePhone () {
    return Viewport.isMobilePhone
  },

  /**
  * Returns true if UI is running on a small screen
  * which requires some adjustments to render the content correctly
   * @type {Boolean}
  */
  get isSmallScreen () {
    return Viewport.isSmallScreen
  },

  /**
   * Returns true if UI is running on a large screen
   * allowing full view
   * @type {Boolean}
   */
  get isNormalScreen () {
    return Viewport.isNormalScreen
  },

  /**
   * Indicates that the applet runs embedded in an iframe, hosted by the ARTup Platform wrapper
   * which takes care of the decorations such as menus and footers
   * @type {Boolean}
   */
  get isEmbeddedApplet () {
    return isEmbeddedInFrame()
  },

  /**
   * Indicates that the applet runs standalone in its own browser window,
   * so it has to render the minimal decorations itself
   * @type {Boolean}
   */
  get isStandaloneApplication () {
    return !isEmbeddedInFrame()
  }
}

/**
 * Checks whether the page is loaded inside an iframe.
 * When the frame comes from another origin, accessing `window.top` throws,
 * which is a sure sign of being embedded as well.
 * @returns {Boolean}
 */
function isEmbeddedInFrame () {
  if (typeof window === 'undefined') return false
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

